/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type BreadcrumbItemType } from '@/components/AZCustomComponent/BreadcrumbSTARC/utils/isBreadcrumbModel';
import { useLocale } from '@/hooks/useLocale';
import { useMemo } from 'react';
import { countryCodes } from '@/constants/locale';
import { blogPrefix } from '../utils/isValidBlogLocale';

type CategoryPathData = {
  categories: string[];
  paths: string[];
};

/**
 * Parses primary category string with format "category1~category2^path1/path2"
 */
const parseStructuredCategory = (primaryCategory: string): CategoryPathData | null => {
  const [categoryString, pathString] = primaryCategory.split('^');

  if (!categoryString || !pathString) {
    return null;
  }

  const categories = categoryString.split('~').map((category) => category.trim());
  const paths = pathString.split('/').filter(Boolean);

  return { categories, paths };
};

/**
 * Parses simple category path format "category1/category2/category3"
 */
const parseSimpleCategory = (primaryCategory: string): CategoryPathData => {
  const categoryPath = primaryCategory.split('/').filter(Boolean);
  return {
    categories: categoryPath,
    paths: categoryPath,
  };
};

/**
 * Builds breadcrumb items from category and path data
 */
const buildCategoryBreadcrumbs = (
  baseUrl: string,
  categoryData: CategoryPathData,
): BreadcrumbItemType[] => {
  const breadcrumbs: BreadcrumbItemType[] = [];
  let currentPath = '';

  const itemCount = Math.min(categoryData.categories.length, categoryData.paths.length);

  for (let i = 0; i < itemCount; i++) {
    currentPath += `/${categoryData.paths[i]}`;
    breadcrumbs.push({
      url: `${baseUrl}${currentPath}`,
      displayName: categoryData.categories[i],
    });
  }

  return breadcrumbs;
};

export const useBlogBreadcrumbData = ({ primaryCategory }: { primaryCategory?: string }) => {
  const locale = useLocale();

  const blogRoot = blogPrefix[locale];
  const displayName =
    locale === countryCodes.us
      ? "Advice & How-To's"
      : locale === countryCodes.mx
        ? 'Expertips'
        : 'Dicas e Como Fazer';

  return useMemo(() => {
    const baseUrl = `${blogRoot}`;
    const breadcrumbItems: BreadcrumbItemType[] = [
      {
        url: baseUrl,
        displayName: displayName,
      },
    ];

    if (!primaryCategory) {
      return breadcrumbItems;
    }
    if (primaryCategory === 'reviewers') {
      breadcrumbItems.push({ displayName: 'Reviewers' });
      return breadcrumbItems;
    }

    const structuredData = parseStructuredCategory(primaryCategory);
    const simpleData = parseSimpleCategory(primaryCategory);
    const categoryBreadcrumbData = structuredData || simpleData;

    const categoryBreadCrumbItems = buildCategoryBreadcrumbs(baseUrl, categoryBreadcrumbData);

    breadcrumbItems.push(...categoryBreadCrumbItems);

    return breadcrumbItems;
  }, [blogRoot, displayName, primaryCategory]);
};
